import React, { useState, useEffect, useRef } from "react";
import styles from "../../styles/tenant.module.css";

interface FeatureObject {
  name: string;
  description?: string;
  enabled: boolean;
  options: Record<string, unknown>;
}

// Separate component for array inputs to manage local editing state
const ArrayOptionInput: React.FC<{
  optionKey: string;
  optionValue: unknown[];
  onChange: (newValue: unknown) => void;
}> = ({ optionKey, optionValue, onChange }) => {
  const [localText, setLocalText] = useState(() => JSON.stringify(optionValue, null, 2));
  const [isValidJson, setIsValidJson] = useState(true);
  const prevValueRef = useRef(optionValue);

  // Sync local text when parent value changes (but not from our own updates)
  useEffect(() => {
    if (prevValueRef.current !== optionValue) {
      setLocalText(JSON.stringify(optionValue, null, 2));
      prevValueRef.current = optionValue;
      setIsValidJson(true);
    }
  }, [optionValue]);

  return (
    <div style={{ marginBottom: '8px' }}>
      <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>
        {optionKey} (Array):
      </label>
      <textarea
        value={localText}
        onChange={(e) => {
          setLocalText(e.target.value);
          try {
            const parsed = JSON.parse(e.target.value);
            if (Array.isArray(parsed)) {
              onChange(parsed);
              prevValueRef.current = parsed;
              setIsValidJson(true);
            } else {
              setIsValidJson(false);
            }
          } catch {
            // Invalid JSON - let user continue typing
            setIsValidJson(false);
          }
        }}
        style={{
          width: '350px',
          height: '80px',
          fontFamily: 'monospace',
          fontSize: '11px',
          border: isValidJson ? '1px solid #ccc' : '2px solid #d32f2f',
          borderRadius: '3px',
          padding: '6px',
          resize: 'vertical'
        }}
      />
      {!isValidJson && (
        <div style={{ color: '#d32f2f', fontSize: '11px', marginTop: '2px' }}>
          Malformed JSON - must be a valid array
        </div>
      )}
    </div>
  );
};

// Separate component for number inputs to handle empty state gracefully
const NumberOptionInput: React.FC<{
  optionKey: string;
  optionValue: number;
  onChange: (newValue: unknown) => void;
}> = ({ optionKey, optionValue, onChange }) => {
  const [localValue, setLocalValue] = useState(String(optionValue));
  const prevValueRef = useRef(optionValue);

  // Sync local value when parent value changes (but not from our own updates)
  useEffect(() => {
    if (prevValueRef.current !== optionValue) {
      setLocalValue(String(optionValue));
      prevValueRef.current = optionValue;
    }
  }, [optionValue]);

  return (
    <div style={{ marginBottom: '6px' }}>
      <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>
        {optionKey}:
      </label>
      <input
        type="number"
        value={localValue}
        onChange={(e) => {
          setLocalValue(e.target.value);
          const val = e.target.value;
          if (val === '' || val === '-') {
            // Allow empty or just minus sign temporarily
            return;
          }
          const parsed = parseFloat(val);
          if (!isNaN(parsed)) {
            onChange(parsed);
            prevValueRef.current = parsed; // Ignore subsequent render syncs from this event
          }
        }}
        onBlur={(e) => {
          // On blur, ensure we have a valid number
          const val = e.target.value;
          if (val === '' || val === '-') {
            onChange(0);
            setLocalValue('0');
          }
        }}
        style={{
          width: '200px',
          padding: '6px',
          border: '1px solid #ccc',
          borderRadius: '3px',
          fontSize: '12px'
        }}
      />
    </div>
  );
};

interface FeatureConfigurationSectionProps {
  value: FeatureObject[];
  onChange: (features: FeatureObject[]) => void;
  applicationName?: string;
  onApplicationChange?: (applicationName: string) => void;
  showApplicationSelector?: boolean;
  existingFeatureNames?: string[]; // Features that already exist and should be filtered out
}

// Feature descriptions for each application
const FEATURE_DESCRIPTIONS: Record<string, Record<string, string>> = {
  "proteus": {
    "Tasks": "Task management integration",
    "BigQuery": "Query and analyze data from BigQuery",
    "Sampling": "Water sampling data integration"
  }
};

// Enhanced feature templates based on backend requirements with application-specific settings
const FEATURE_TEMPLATES: Record<string, Record<string, unknown>> = {
  "leadCAST": {
    "Inventory": {
      "allGalvRequiresReplacement": false
    },
    "Public Map": {
      "enabled": false,
      "colorOption": 1,
      "latitude": null,
      "longitude": null,
      "zoom": null,
      "url": "{{LEADCAST_BASEPATH}}/publicMap?tenantName={{TENANT_NAME}}",
      "displayName": "Public Map",
      "disclaimerOptions": {
        "disclaimer": "[{\"type\":\"numbered-list\",\"children\":[{\"type\":\"list-item\",\"children\":[{\"text\":\"Note to All\",\"underline\":true,\"bold\":true},{\"text\":\": If you feel this information is incorrect, please contact us at support@trinnex.io. The materials provided are based on the best available information. Your local water provider cannot guarantee the accuracy of these records.\"}]}]}]",
        "disclaimerDisplayEnabled": true
      },
      "replacementSignUpEnabled": false,
      "sampleResultsDisabled": false
    },
    "Sampling": {
      "customSamplingProviderEnabled": false,
      "defaultSamplingProviderEnabled": true,
      "samplingResultsEmailsEnabled": false,
      "samplePoolPerc90Exceedance": false,
      "unreturnedSamplingKitEmailsEnabled": false,
      "exceedanceThresholds": {
        "lead": 15,
        "copper": 1300
      },
      "tapSampleFormSettings": {
        "minStagnationHours": 6,
        "maxStagnationHours": 48,
        "repairMonths": 6
      }
    },
    "File Storage": { 
      "fileStorageBuckets": {
        "inspectionImages": {
          "name": "{{TENANT_NAME}}-inspectionimages"
        },
        "rightOfEntry": {
          "name": "{{TENANT_NAME}}-rightofentry"
        },
        "samplingInstructions": {
          "name": "{{TENANT_NAME}}-samplinginstructions"
        },
        "tapSampleForms": {
          "name": "{{TENANT_NAME}}-tapsampleforms"
        }
      }
    },
    "Right of Entry": {
      "enabled": false,
      "configuration": "Off",
      "configurationOptions": [
        {
          "name": "On",
          "label": "On",
          "description": "Right of entry is required for properties undergoing replacement."
        },
        {
          "name": "Off",
          "label": "Off",
          "description": "Right of entry is not required for properties undergoing replacement."
        },
        {
          "name": "Private-side only",
          "label": "Private-side only",
          "description": "Right of entry is required for properties undergoing replacement on the private side or property interior."
        }
      ],
      "unverifiedUserCanSubmit": false
    },
    "Replacement": {},
    "Predict": {
      "enabled": false
    },
    "Customer Portal": {
      "enabled": true,
      "url": "{{LEADCAST_BASEPATH}}/login?tenantName={{TENANT_NAME}}",
      "displayName": "Customer portal"
    },
    "Field Inspection": {
      "enabled": true,
      "url": "{{LEADCAST_BASEPATH}}/FieldInspection",
      "displayName": "Field inspection map"
    }
  },
  "pipeCAST": {
    "CapacityAssessment": {
      "Enabled": true
    },
    "Permitting": {
      "Enabled": true,
      "IsDefault": true
    },
    "Reservations": {
      "Enabled": true
    },
    "PermittingConfiguration": {
      "requireExternalPermitId": false,
      "requireExternalReservationId": false
    },
    "CapacityAssessmentUnitsConfiguration": {
      "volumeFlowUnit": 46
    },
    "CapacityAssessmentResultsConfig": {
      "ReportingMethod": 1,
      "LinkFlowRatioPassThreshold": 1,
      "LinkFlowRatioWarningThreshold": 0.7,
      "LinkDepthRatioPassThreshold": 1,
      "LinkDepthRatioWarningThreshold": 0.7,
      "RimDepthPassThreshold": 1,
      "RimDepthWarningThreshold": 3,
      "CapacityAssessmentTracingDirection": 4,
      "ExcludePassedAssets": false,
      "ExcludeWarnedAssets": false,
      "BaselineFailureSuppressionEnabled": false
    }
  },
  "epiCAST": {
    "data-analysis": {
      "enableRealTimeAnalytics": true,
      "dataRetentionDays": 365,
      "exportFormats": ["CSV", "PDF", "Excel"]
    },
    "reporting": {
      "enableAutomatedReports": true,
      "reportFrequency": "weekly",
      "customDashboards": true
    },
    "dashboard": {
      "enableCustomDashboards": true,
      "maxDashboards": 10,
      "autoRefreshInterval": 300
    },
    "alerts": {
      "enableEmailAlerts": true,
      "enableSMSAlerts": false,
      "thresholdSettings": {
        "critical": 90,
        "warning": 75
      }
    }
  },
  "proteus": {
    "Tasks": {},
    "BigQuery": {},
    "Sampling": {}
  },
  "assetCAST": {},
  "datacast": {
    "processModelTrainingSimulator": {}
  }
};

const FeatureConfigurationSection: React.FC<FeatureConfigurationSectionProps> = ({
  value = [],
  onChange,
  applicationName = "leadCAST",
  onApplicationChange,
  showApplicationSelector = true,
  existingFeatureNames = []
}) => {
  const [features, setFeatures] = useState<FeatureObject[]>(value);
  const [featuresError, setFeaturesError] = useState<string>("");
  const [selectedApplication, setSelectedApplication] = useState<string>(applicationName);
  const prevApplicationRef = React.useRef<string | null>(null);

  // Latest-ref pattern to avoid onChange dependency in useEffect
  const onChangeRef = React.useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const availableFeatureNames = Object.keys(FEATURE_TEMPLATES[selectedApplication] || {});
  const availableApplications = Object.keys(FEATURE_TEMPLATES);

  useEffect(() => {
    setFeatures(value);
  }, [value]);

  useEffect(() => {
    if (prevApplicationRef.current && prevApplicationRef.current !== applicationName) {
      setFeatures([]);
      onChangeRef.current([]);
      setFeaturesError("");
    }
    prevApplicationRef.current = applicationName;
    setSelectedApplication(applicationName);
  }, [applicationName]);

  const handleApplicationChange = (newApplication: string) => {
    setSelectedApplication(newApplication);
    if (onApplicationChange) {
      onApplicationChange(newApplication);
    }
    // Clear features when application changes to avoid confusion
    setFeatures([]);
    onChangeRef.current([]);
    setFeaturesError("");
  };

  const validateFeatures = (featuresToValidate: FeatureObject[]): boolean => {
    try {
      const featureNames = new Set();
      
      for (let i = 0; i < featuresToValidate.length; i++) {
        const feature = featuresToValidate[i];
        
        if (!feature.name || typeof feature.name !== "string" || feature.name.trim() === "") {
          setFeaturesError(`Feature at index ${i} is missing required 'name' property or name is empty.`);
          return false;
        }
        
        if (typeof feature.enabled !== "boolean") {
          setFeaturesError(`Feature '${feature.name}' is missing required 'enabled' property or it's not a boolean.`);
          return false;
        }
        
        if (!feature.options || typeof feature.options !== "object") {
          setFeaturesError(`Feature '${feature.name}' is missing required 'options' property or it's not an object.`);
          return false;
        }
        
        if (featureNames.has(feature.name)) {
          setFeaturesError(`Duplicate feature name: '${feature.name}'. Each feature must have a unique name.`);
          return false;
        }
        
        featureNames.add(feature.name);
      }
      
      setFeaturesError("");
      return true;
    } catch (error) {
      setFeaturesError("Invalid feature structure");
      return false;
    }
  };

  const updateFeatures = (updatedFeatures: FeatureObject[]) => {
    if (validateFeatures(updatedFeatures)) {
      setFeatures(updatedFeatures);
      onChangeRef.current(updatedFeatures);
    }
  };

  const getDefaultEnabledState = (featureName: string, application?: string): boolean => {
    const app = application || selectedApplication;

    // Handle features that exist in multiple applications
    if (featureName === 'Sampling') {
      if (app === 'leadCAST') return true;
      if (app === 'proteus') return false;
    }

    // Define default enabled states based on backend logic
    switch (featureName) {
      // leadCAST features
      case 'Inventory':
        return true;
      case 'Public Map':
        return false;
      case 'File Storage':
        return true;
      case 'Right of Entry':
        return false; // Will be set to true if licenseLevel is 'Plus' in actual implementation
      case 'Replacement':
        return false;
      case 'Predict':
        return false;
      case 'Customer Portal':
        return true;
      case 'Field Inspection':
        return true;
      // pipeCAST features
      case 'CapacityAssessment':
        return true;
      case 'Permitting':
        return true;
      case 'Reservations':
        return true;
      case 'PermittingConfiguration':
        return true;
      case 'CapacityAssessmentUnitsConfiguration':
        return true;
      case 'CapacityAssessmentResultsConfig':
        return true;
      // proteus features
      case 'Tasks':
        return false;
      case 'BigQuery':
        return false;
      // datacast features
      case 'processModelTrainingSimulator':
        return false;
      default:
        return false;
    }
  };

  const addFeature = () => {
    const usedNames = features.map(f => f.name);
    const availableName = availableFeatureNames.find(name => !usedNames.includes(name));

    if (!availableName) {
      setFeaturesError("All available features have been added");
      return;
    }

    const newFeature: FeatureObject = {
      name: availableName,
      description: FEATURE_DESCRIPTIONS[selectedApplication]?.[availableName],
      enabled: getDefaultEnabledState(availableName),
      options: { ...(FEATURE_TEMPLATES[selectedApplication][availableName] as Record<string, unknown>) }
    };

    updateFeatures([...features, newFeature]);
  };

  const selectAllFeatures = () => {
    // Get only the remaining features that haven't been added yet
    const remainingFeatureNames = getAvailableFeatureNames();
    const newFeatures: FeatureObject[] = remainingFeatureNames.map(featureName => ({
      name: featureName,
      description: FEATURE_DESCRIPTIONS[selectedApplication]?.[featureName],
      enabled: getDefaultEnabledState(featureName),
      options: { ...(FEATURE_TEMPLATES[selectedApplication][featureName] as Record<string, unknown>) }
    }));

    // Add the new features to existing features instead of replacing them
    updateFeatures([...features, ...newFeatures]);
  };

  const unselectAllFeatures = () => {
    updateFeatures([]);
  };

  const toggleSelectAllFeatures = () => {
    const remainingFeatures = getAvailableFeatureNames();
    if (remainingFeatures.length > 0) {
      selectAllFeatures(); // Add remaining features
    } else {
      unselectAllFeatures(); // Remove all features if all are already added
    }
  };

  const removeFeature = (index: number) => {
    const updatedFeatures = features.filter((_, i) => i !== index);
    updateFeatures(updatedFeatures);
  };

  const updateFeatureName = (index: number, newName: string) => {
    const updatedFeatures = features.map((f, i) =>
      i === index
        ? {
            ...f,
            name: newName,
            description: FEATURE_DESCRIPTIONS[selectedApplication]?.[newName],
            options: FEATURE_TEMPLATES[selectedApplication]?.[newName]
              ? { ...(FEATURE_TEMPLATES[selectedApplication][newName] as Record<string, unknown>) }
              : f.options,
          }
        : f
    );
    updateFeatures(updatedFeatures);
  };

  const updateFeatureEnabled = (index: number, enabled: boolean) => {
    const updatedFeatures = features.map((f, i) =>
      i === index ? { ...f, enabled } : f
    );
    updateFeatures(updatedFeatures);
  };

  const updateOptionValue = (featureIndex: number, optionKey: string, newValue: unknown) => {
    const updatedFeatures = features.map((f, i) =>
      i === featureIndex
        ? { ...f, options: { ...f.options, [optionKey]: newValue } }
        : f
    );
    updateFeatures(updatedFeatures);
  };

  const updateDeepOptionValue = (featureIndex: number, keysPath: string[], newValue: unknown) => {
    const updateDeep = (obj: Record<string, unknown>, path: string[], value: unknown): Record<string, unknown> => {
      const [first, ...rest] = path;
      return {
        ...obj,
        [first]: rest.length === 0
          ? value
          : updateDeep(
              (typeof obj?.[first] === 'object' && obj?.[first] !== null && !Array.isArray(obj?.[first]))
                ? (obj?.[first] as Record<string, unknown>)
                : {},
              rest,
              value
            )
      };
    };
    const updatedFeatures = features.map((f, i) =>
      i === featureIndex ? { ...f, options: updateDeep(f.options, keysPath, newValue) } : f
    );
    updateFeatures(updatedFeatures);
  };

  const renderOptionInput = (
    featureIndex: number,
    optionKey: string,
    optionValue: unknown,
    parentKeys: string[] = []
  ): JSX.Element => {
    const handleChange = (newValue: unknown) => {
      if (parentKeys.length > 0) {
        updateDeepOptionValue(featureIndex, [...parentKeys, optionKey], newValue);
      } else {
        updateOptionValue(featureIndex, optionKey, newValue);
      }
    };

    if (typeof optionValue === 'boolean') {
      return (
        <div key={optionKey} style={{ marginBottom: '4px' }}>
          <label style={{ fontSize: '12px', display: 'flex', alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={optionValue}
              onChange={(e) => handleChange(e.target.checked)}
              style={{ marginRight: '8px' }}
            />
            {optionKey}
          </label>
        </div>
      );
    }

    if (typeof optionValue === 'number') {
      return (
        <NumberOptionInput
          key={optionKey}
          optionKey={optionKey}
          optionValue={optionValue}
          onChange={handleChange}
        />
      );
    }

    if (typeof optionValue === 'string' || optionValue === null) {
      return (
        <div key={optionKey} style={{ marginBottom: '6px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', display: 'block', marginBottom: '2px' }}>{optionKey}:</label>
          <input
            type="text"
            value={optionValue || ''}
            onChange={(e) => handleChange(e.target.value || null)}
            style={{
              width: '250px',
              padding: '6px',
              border: '1px solid #ccc',
              borderRadius: '3px',
              fontSize: '12px'
            }}
          />
        </div>
      );
    }

    if (Array.isArray(optionValue)) {
      return (
        <ArrayOptionInput
          key={optionKey}
          optionKey={optionKey}
          optionValue={optionValue}
          onChange={handleChange}
        />
      );
    }

    if (typeof optionValue === 'object' && optionValue !== null) {
      const objValue = optionValue as Record<string, unknown>;
      return (
        <div key={optionKey} style={{ marginBottom: '12px' }}>
          <label style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '6px', display: 'block' }}>
            {optionKey}:
          </label>
          <div style={{
            border: '1px solid #ddd',
            padding: '10px',
            borderRadius: '3px',
            backgroundColor: '#f8f8f8',
            marginLeft: '15px',
            maxWidth: '500px'
          }}>
            {Object.keys(objValue).map(subKey =>
              renderOptionInput(featureIndex, subKey, objValue[subKey], [...parentKeys, optionKey])
            )}
          </div>
        </div>
      );
    }

    return <div key={optionKey}>Unsupported type for {optionKey}</div>;
  };


  const getAvailableFeatureNames = () => {
    const usedNames = features.map(f => f.name);
    return availableFeatureNames.filter(name => !usedNames.includes(name));
  };

  return (
    <div>
      <hr />
      <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px", paddingTop: "15px", flexWrap: "wrap" }}>
        <h3 style={{ margin: 0 }}>Features</h3>
        
        {showApplicationSelector && (
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <label style={{ fontSize: "12px", fontWeight: "bold" }}>Application:</label>
            <select
              value={selectedApplication}
              onChange={(e) => handleApplicationChange(e.target.value)}
              style={{
                padding: "6px 8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "12px"
              }}
            >
              {availableApplications.map(app => (
                <option key={app} value={app}>{app}</option>
              ))}
            </select>
          </div>
        )}
        
        <button
          type="button"
          onClick={toggleSelectAllFeatures}
          disabled={availableFeatureNames.length === 0}
          style={{
            padding: "8px 12px",
            backgroundColor: availableFeatureNames.length > 0 ?
              (getAvailableFeatureNames().length > 0 ? "rgb(75, 100, 194)" : "rgb(194, 75, 75)") : "#ccc",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: availableFeatureNames.length > 0 ? "pointer" : "not-allowed",
            fontSize: "12px"
          }}
        >
          {getAvailableFeatureNames().length > 0 ?
            `✅ Select All Features (${getAvailableFeatureNames().length})` :
            "❌ Unselect All Features"}
        </button>
        <button
          type="button"
          onClick={addFeature}
          disabled={getAvailableFeatureNames().length === 0}
          style={{
            padding: "8px 12px",
            backgroundColor: getAvailableFeatureNames().length > 0 ? "rgb(75, 100, 194)" : "#ccc",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: getAvailableFeatureNames().length > 0 ? "pointer" : "not-allowed",
            fontSize: "12px"
          }}
        >
          + Add Feature
        </button>
      </div>

      {featuresError && (
        <div style={{ color: "red", fontSize: "14px", marginBottom: "10px", padding: "8px", backgroundColor: "#ffe6e6", borderRadius: "4px" }}>
          {featuresError}
        </div>
      )}

      <div style={{ border: "1px solid #ddd", padding: "15px", borderRadius: "5px", backgroundColor: "#f9f9f9" }}>
        {features.length === 0 ? (
          <p style={{ textAlign: "center", color: "#666", margin: "15px 0" }}>
            No features configured yet. Use &quot;Add Feature&quot; to get started.
          </p>
        ) : (
          features.map((feature, index) => (
            <div key={index} style={{ 
              border: "1px solid #eee", 
              padding: "15px", 
              margin: "10px 0", 
              borderRadius: "5px",
              backgroundColor: "white",
              boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
            }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h4 style={{ margin: 0, color: "#333" }}>Feature {index + 1}</h4>
                <button
                  type="button"
                  onClick={() => removeFeature(index)}
                  style={{
                    backgroundColor: "rgb(194, 75, 75)",
                    color: "white",
                    border: "none",
                    borderRadius: "4px",
                    padding: "6px 12px",
                    cursor: "pointer",
                    fontSize: "12px"
                  }}
                >
                  ✕ Remove
                </button>
              </div>
              
              <div style={{ display: "flex", alignItems: "flex-end", gap: "20px", marginBottom: "15px" }}>
                <div style={{ flex: "0 0 280px" }}>
                  <label className={styles["label"]}>Feature Name:</label>
                  <select
                    value={feature.name}
                    onChange={(e) => updateFeatureName(index, e.target.value)}
                    className={styles["input"]}
                    style={{ width: "100%" }}
                  >
                    {feature.name && !availableFeatureNames.includes(feature.name) && (
                      <option key={feature.name} value={feature.name}>{feature.name}</option>
                    )}
                    {availableFeatureNames.map(featureName => {
                      const isCurrentFeature = feature.name === featureName;
                      const isUsedByOther = features.some((f, i) => i !== index && f.name === featureName);

                      if (isCurrentFeature || !isUsedByOther) {
                        return <option key={featureName} value={featureName}>{featureName}</option>;
                      }
                      return null;
                    })}
                  </select>
                </div>
                
                <div style={{ display: "flex", alignItems: "center", paddingBottom: "8px" }}>
                  <input
                    type="checkbox"
                    checked={feature.enabled}
                    onChange={(e) => updateFeatureEnabled(index, e.target.checked)}
                    style={{ marginRight: "8px" }}
                  />
                  <label className={styles["label"]} style={{ margin: 0, paddingTop: 0, paddingBottom: 0 }}>Enabled</label>
                </div>
              </div>
              
              <div style={{ marginTop: "10px" }}>
                <label className={styles["label"]} style={{ marginBottom: "8px", display: "block" }}>
                  Options:
                </label>
                <div style={{ 
                  border: '1px solid #ddd', 
                  padding: '12px', 
                  borderRadius: '4px', 
                  backgroundColor: '#fafafa',
                  maxWidth: '600px'
                }}>
                  {feature.options && typeof feature.options === 'object' && Object.keys(feature.options).length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {Object.keys(feature.options).map(optionKey => 
                        renderOptionInput(index, optionKey, feature.options[optionKey])
                      )}
                    </div>
                  ) : (
                    <p style={{ color: '#666', fontSize: '12px', fontStyle: 'italic', margin: 0 }}>
                      No options available for this feature.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );
};

export default FeatureConfigurationSection;